import React, { memo } from 'react';
import cn from 'classnames';
import style from '../styles/keyboard.module.scss';
import { TouchableButton } from '@components/common/Touchable/index';
import { getKeyBaseClass, getKeyStyle, getKeyWidth } from '../utils/keyClassNames';

/**
 * A single keyboard key button.
 *
 * This is the true memo boundary — when a user presses a key, only the
 * parent KeyboardContainer re-renders (to update layout state). Since every
 * prop here is a primitive or a stable reference, KeyboardKey components
 * that aren't affected by the change will NOT re-render.
 *
 * Class computation uses pure functions (no hooks), so there's no closure
 * recreation on each render.
 */
const KeyboardKey = memo(({
  keyToken,
  rowIndex,
  type,
  screenType,
  btnSize,
  isCounter,
  isDriveThru,
  isCallCenter,
  layout,
  allLayouts,
  handleClick,
  display,
  handleLongPress,
  hideDomainNames,
  serverAppTheme,
  buttonTheme,
  tableKeyHeight,
  keyboardHeight,
  isDiscount,
  employeeCheck,
  spacebtn,
  donebuttontheme,
}) => {
  const isFirstRow = rowIndex === 0;

  const baseClass = getKeyBaseClass(type, screenType);
  const sizeClass = getKeyStyle(
    keyToken, type, screenType, tableKeyHeight, isDiscount, employeeCheck,
    keyboardHeight, isCounter, isDriveThru, isCallCenter, spacebtn, donebuttontheme,
  );
  const widthClass = rowIndex > 3
    ? getKeyWidth(keyToken, layout, allLayouts, isCounter, isDriveThru, isCallCenter, type)
    : 'w-100';

  const spacingClass =
    isFirstRow && type === 'textWithoutNumeric'
      ? 'h-100 px-1 py-1 mr-1'
      : screenType === 'couponPopUp'
      ? 'h-100 px-1 py-1 mr-1'
      : 'h-100 px-1 py-1 mr-1 mt-1';

  // Apply corner class to ALL keys in rows 0 and 3 — CSS :first-child/:last-child
  // selectors then pick only the edge buttons within each row.
  const cornerClass = cn(
    rowIndex === 0 && type === 'number' ? style.cornerButtonTop : '',
    rowIndex === 3 && type === 'number' ? style.cornerButtonButtom : '',
  );

  const keyStyle = {
    backgroundColor:
      keyToken === '{done}' || keyToken === '{continue}' || keyToken === '{cancel}'
        ? '#2541A4'
        : serverAppTheme?.keyBoard_key_Color,
    color:
      keyToken === '{done}' || keyToken === '{continue}' || keyToken === '{cancel}'
        ? '#FFF'
        : serverAppTheme?.primary_text,
    flex: type === 'textWithoutNumeric' && keyToken === '{alt}' ? '0 0 13%' : undefined,
    maxWidth: type === 'textWithoutNumeric' && keyToken === '{alt}' ? '13%' : undefined,
    lineHeight:
      screenType === 'giftCard' && type === 'textWithoutNumeric' && (!isCounter || !isDriveThru || !isCallCenter)
        ? '2.5'
        : undefined,
  };

  const className = cn(
    btnSize,
    spacingClass,
    baseClass,
    sizeClass,
    widthClass,
    type === 'textWithNumericEmail' && keyToken === '{special}' && 'col-2',
    (type === 'textWithoutNumeric' || type === 'textDefaultCaps' || type === 'numericAlpha') && 'rounded',
    cornerClass,
    type === 'textWithNumericEmail' &&
      (screenType === 'loyalty' || screenType === 'textreceiptPopUp') &&
      style.textEmailSize,
    buttonTheme || '',
    'd-flex justify-content-center align-items-center border-0',
  );

  const label = display[keyToken]
    ? display[keyToken]
    : keyToken === '{swipe}'
    ? ''
    : keyToken;

  return (
    <TouchableButton
      className={className}
      style={keyStyle}
      type="button"
      onClick={event => { handleClick(keyToken); event.preventDefault(); }}
      rippleEffect={true}
      longPress={keyToken === '.com'}
      handleLongPress={handleLongPress}
      hideDomainNames={hideDomainNames}
    >
      {label}
    </TouchableButton>
  );
});

KeyboardKey.displayName = 'KeyboardKey';

export default KeyboardKey;
